import * as React from 'react';

import {ChatPreview} from 'shells/chat/chat-preview';
import {MinimizedPreview} from 'shells/chat/minimized-preview';
import {colors} from 'shells/colors/colors';
import {PageContentPreviewable} from 'shells/layout/page-content-previewable/page-content-previewable';
import {PreviewableSection} from 'shells/layout/page-content-previewable/previewable-section';
import {LoadingText} from 'shells/loading-placeholder/loading-text';
import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';

import type {ChatVariantEnum, NudgeDeviceVisibilityEnum} from 'nutshell-graphql-types';
import type {ChatSettingsLookAndFeelInput} from 'nutshell-graphql-types';

import {useGetAppearanceSettings} from '../../graphql/hooks/use-get-appearance-settings';
import {useGetPrimaryContextDocument} from '../../graphql/hooks/use-get-primary-context-document';
import {useUpdateAppearanceSettings} from '../../graphql/hooks/use-update-appearance-settings';

import {SAVE_DELAY, CHAT_VARIANT} from '../consts';
import type {SaveStateKey} from '../consts';
import {getChatbotIcon, getChatWidgetIcon} from '../helpers';
import {SettingsContentWrapper} from '../settings-content-wrapper';

import {DisabledPathsConfigurationSection} from './disabled-paths-configuration-section';
import {EngagementBarActionsSection} from './engagement-bar-actions-section';
import {MinimizedViewSection} from './minimized-view-section';

const PREVIEWABLE_SECTIONS = {
    ACTIONS: 'actions',
    MINIMIZED_VIEW: 'minimized-view',
} as const;

type Props = {
    hasNoKnowledgeContent: boolean;
};

export function EngagementBarOptions(props: Props) {
    const {hasNoKnowledgeContent} = props;
    const [disabledPaths, setDisabledPaths] = React.useState<string[]>([]);
    const [chatVariant, setChatVariant] = React.useState<ChatVariantEnum>(CHAT_VARIANT.CHAT);
    const [isNudgeEnabled, setIsNudgeEnabled] = React.useState<boolean>(false);
    const [nudgeCallToAction, setNudgeCallToAction] = React.useState<string>('');
    const [nudgeDeviceVisibility, setNudgeDeviceVisibility] =
        React.useState<NudgeDeviceVisibilityEnum>('DESKTOP_ONLY');
    const [previewedSectionId, setPreviewedSectionId] = React.useState<string | null | undefined>(
        null
    );

    const {chatSettings, isLoading, refetch: refetchSettings} = useGetAppearanceSettings();
    const {primaryContextDocument, isLoading: isLoadingSuggestions} =
        useGetPrimaryContextDocument();

    const suggestions = (primaryContextDocument && primaryContextDocument.aiSuggestions) || [];

    React.useEffect(() => {
        if (chatSettings) {
            const {lookAndFeel} = chatSettings;
            setChatVariant(lookAndFeel.chatVariant);
            setIsNudgeEnabled(lookAndFeel.nudgeSettings.isEnabled);
            setNudgeCallToAction(lookAndFeel.nudgeSettings.callToAction);
            setNudgeDeviceVisibility(lookAndFeel.nudgeSettings.deviceVisibility);
            setDisabledPaths(
                lookAndFeel && lookAndFeel.disabledPaths ? lookAndFeel.disabledPaths : []
            );
        }
    }, [chatSettings]);

    const {updateAppearanceSettings} = useUpdateAppearanceSettings();

    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const {toastState} = useManageSaveState(isSaving, SAVE_DELAY);
    const [savingToastKey, setSavingToastKey] = React.useState<SaveStateKey | null | undefined>(
        null
    );

    const handleSave = (updates: ChatSettingsLookAndFeelInput, toastKey: SaveStateKey) => {
        setIsSaving(true);
        setSavingToastKey(toastKey);

        return updateAppearanceSettings(updates).then(() => {
            refetchSettings();
            setIsSaving(false);
            setTimeout(() => {
                setSavingToastKey(null);
            }, 1000);
        });
    };

    // Slight delay on this to allow actual state to change before the preview changes
    const handlePreviewSectionFocus = (sectionId: string) => {
        setTimeout(() => {
            setPreviewedSectionId(sectionId);
        }, 100);
    };

    const actionBarEnabled = chatVariant === CHAT_VARIANT.ACTIONS;

    const getPreview = () => {
        if (!chatSettings) {
            return (
                <div className='flex justify-center align-center full-width full-height'>
                    <LoadingText height={500} width={305} color={colors.plumLt} />
                </div>
            );
        }

        switch (previewedSectionId) {
            case PREVIEWABLE_SECTIONS.MINIMIZED_VIEW:
                return (
                    <MinimizedPreview
                        icon={getChatWidgetIcon(
                            chatSettings.lookAndFeel.icon.type,
                            chatSettings.lookAndFeel.icon.url
                        )}
                        primaryColor={chatSettings.lookAndFeel.color}
                        secondaryColor={chatSettings.lookAndFeel.secondaryColor}
                        actionBarEnabled={actionBarEnabled || false}
                        actionBarShownActions={chatSettings.lookAndFeel.actionBarShownActions}
                        actions={chatSettings.actions}
                        appearanceCallToAction={chatSettings.lookAndFeel.nudgeSettings.callToAction}
                        showNudge={isNudgeEnabled}
                        suggestions={suggestions}
                        isLoadingSuggestions={isLoadingSuggestions}
                    />
                );
            case PREVIEWABLE_SECTIONS.ACTIONS:
            default:
                return (
                    <ChatPreview
                        headerTitle={chatSettings.lookAndFeel.nameDisplay}
                        backgroundColor={chatSettings.lookAndFeel.color}
                        status='appearance'
                        icon={getChatWidgetIcon(
                            chatSettings.lookAndFeel.icon.type,
                            chatSettings.lookAndFeel.icon.url
                        )}
                        conversationStarter={chatSettings.lookAndFeel.conversationStarter}
                        showPoweredBy={chatSettings.lookAndFeel.showPoweredBy}
                        showDisclaimer={chatSettings.lookAndFeel.disclaimerSettings.isEnabled}
                        disclaimerText={chatSettings.lookAndFeel.disclaimerSettings.message}
                        secondaryColor={chatSettings.lookAndFeel.secondaryColor}
                        chatbotName={chatSettings.lookAndFeel.chatbotName}
                        chatbotIcon={getChatbotIcon(chatSettings.lookAndFeel.chatbotIconEnum)}
                        actionBarEnabled={true}
                        actionBarShownActions={chatSettings.lookAndFeel.actionBarShownActions}
                        actions={chatSettings.actions}
                        showNudge={isNudgeEnabled}
                        suggestions={suggestions}
                        isLoadingSuggestions={isLoadingSuggestions}
                    />
                );
        }
    };

    const isLoadingChatSettings = !chatSettings || !chatSettings.lookAndFeel;

    return (
        <SettingsContentWrapper>
            <PageContentPreviewable
                getPreview={getPreview}
                leftContent={
                    <div className='justify-sb flex flex-dir-col full-height mb-32 gap-32 pr-8'>
                        <PreviewableSection
                            sectionId={PREVIEWABLE_SECTIONS.ACTIONS}
                            onFocus={handlePreviewSectionFocus}
                        >
                            <EngagementBarActionsSection
                                isLoading={isLoadingChatSettings}
                                chatVariant={chatVariant}
                                setChatVariant={setChatVariant}
                                actionBarShownActions={
                                    chatSettings &&
                                    chatSettings.lookAndFeel &&
                                    chatSettings.lookAndFeel.actionBarShownActions
                                }
                                actionBarHiddenActions={
                                    chatSettings &&
                                    chatSettings.lookAndFeel &&
                                    chatSettings.lookAndFeel.actionBarHiddenActions
                                }
                                actions={chatSettings && chatSettings.actions}
                                handleSave={handleSave}
                                toastState={toastState}
                                savingToastKey={savingToastKey}
                            />
                        </PreviewableSection>
                        <PreviewableSection
                            sectionId={PREVIEWABLE_SECTIONS.MINIMIZED_VIEW}
                            onFocus={handlePreviewSectionFocus}
                        >
                            <MinimizedViewSection
                                isLoading={isLoading}
                                handleSave={handleSave}
                                isNudgeEnabled={isNudgeEnabled}
                                hasNoKnowledgeContent={hasNoKnowledgeContent}
                                setIsNudgeEnabled={setIsNudgeEnabled}
                                nudgeCallToAction={nudgeCallToAction}
                                nudgeDeviceVisibility={nudgeDeviceVisibility}
                                setNudgeDeviceVisibility={setNudgeDeviceVisibility}
                                toastState={toastState}
                                savingToastKey={savingToastKey}
                            />
                        </PreviewableSection>
                        <DisabledPathsConfigurationSection
                            isLoading={isLoading}
                            disabledPaths={disabledPaths}
                            handleSave={handleSave}
                            toastState={toastState}
                            savingToastKey={savingToastKey}
                        />
                    </div>
                }
            />
        </SettingsContentWrapper>
    );
}
